/**
 * @fileoverview Contatos compartilhados (vCard) de uma mensagem do chat: lê o
 * cartão salvo (dataJson) e, sem ele, o texto da mensagem. Espelha o
 * backend/src/helpers/vcard.ts.
 *
 * Ordem: dataJson.message.contactsArrayMessage → contactMessage →
 * dataJson._data (encaminhamento pela WAHA) → texto "Nome:/Número:" →
 * vCard cru no texto (mensagens antigas).
 */

import { formatPhoneNumber } from "./formatPhoneNumber";

// RFC 6350: linha que começa com espaço/tab continua a anterior.
const unfold = (vcard) =>
  String(vcard || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\n[ \t]/g, "")
    .split("\n");

// Uma passada só: "\\n" vira espaço e "\\," "\\;" "\\\\" voltam ao caractere.
const unescapeValue = (value) =>
  value.replace(/\\([nN,;\\])/g, (_m, c) => (c === "n" || c === "N" ? " " : c)).trim();

// Separa por ";" que não esteja escapado (sem lookbehind, que Safari antigo não tem).
const splitUnescaped = (value) => {
  const parts = [];
  let current = "";
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (ch === "\\" && i + 1 < value.length) {
      current += ch + value[i + 1];
      i += 1;
    } else if (ch === ";") {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts;
};

const splitLine = (line) => {
  const idx = line.indexOf(":");
  if (idx <= 0) return null;
  const [rawName, ...params] = line.slice(0, idx).split(";");
  return {
    name: rawName.replace(/^item\d+\./i, "").toUpperCase(),
    params: params.join(";"),
    value: line.slice(idx + 1),
  };
};

// N: Sobrenome;Nome;Meio;Prefixo;Sufixo
const nameFromN = (value) => {
  const [family = "", given = "", middle = "", prefix = "", suffix = ""] = splitUnescaped(value).map(unescapeValue);
  return [prefix, given, middle, family, suffix].filter(Boolean).join(" ").trim();
};

// TEL sem waid: com "+" já tem o país; sem ele, 10/11 dígitos é brasileiro com DDD.
const telToNumber = (value) => {
  const text = String(value || "").trim();
  const digits = text.replace(/\D/g, "");
  if (!digits || text.startsWith("+")) return digits;
  const national = digits.replace(/^0+/, "");
  return national.length === 10 || national.length === 11 ? `55${national}` : digits;
};

export const parseVCard = (vcard) => {
  let fn = "";
  let n = "";
  let business = "";
  let org = "";
  let waid = "";
  let firstTel = "";

  for (const line of unfold(vcard)) {
    const part = splitLine(line);
    if (!part) continue;
    if (part.name === "FN" && !fn) fn = unescapeValue(part.value);
    else if (part.name === "N" && !n) n = nameFromN(part.value);
    else if (part.name === "X-WA-BIZ-NAME" && !business) business = unescapeValue(part.value);
    else if (part.name === "ORG" && !org) org = unescapeValue(splitUnescaped(part.value)[0] || "");
    else if (part.name === "TEL") {
      const id = part.params.match(/waid=(\d+)/i);
      if (id && !waid) waid = id[1];
      if (!firstTel) firstTel = telToNumber(part.value);
    }
  }

  return { name: fn || n || business || org, number: waid || firstTel };
};

const fromCards = (cards) =>
  (cards || [])
    .filter(Boolean)
    .map((card) => {
      const parsed = parseVCard(card.vcard || card.vCard || card.Vcard);
      return {
        name: parsed.name || String(card.displayName || card.DisplayName || "").trim(),
        number: parsed.number,
      };
    })
    .filter((c) => c.name || c.number);

// Mensagem no formato do proto: contactsArrayMessage (todos) ou contactMessage.
const fromMessageBag = (bag) => {
  if (!bag || typeof bag !== "object") return [];
  const array = bag.contactsArrayMessage?.contacts || bag.ContactsArrayMessage?.Contacts;
  if (Array.isArray(array) && array.length) return fromCards(array);
  const single = bag.contactMessage || bag.ContactMessage;
  return single ? fromCards([single]) : [];
};

const FORWARDED_PREFIX = /^_Encaminhada_:\s*\n?/;

// "Contato compartilhado:\nNome: X\nNúmero: N" (ou vários blocos).
const fromBody = (body) => {
  const text = String(body || "").replace(FORWARDED_PREFIX, "");
  if (!/^Contatos? compartilhados?/i.test(text)) return [];
  const contacts = [];
  let current = null;
  text.split("\n").forEach((line) => {
    const nameMatch = line.match(/^Nome:\s*(.*)$/);
    const numberMatch = line.match(/^Número:\s*(.*)$/);
    if (nameMatch) {
      current = { name: nameMatch[1].trim(), number: "" };
      contacts.push(current);
    } else if (numberMatch && current) {
      // O texto salvo já traz o número com o país (waid): só os dígitos.
      current.number = numberMatch[1].replace(/\D/g, "");
    }
  });
  return contacts.filter((c) => c.name || c.number);
};

const fromRawVCards = (body) =>
  String(body || "")
    .split(/END:VCARD/i)
    .filter((chunk) => /BEGIN:VCARD/i.test(chunk))
    .map((chunk) => parseVCard(chunk))
    .filter((c) => c.name || c.number);

const parseJson = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Contatos ({ name, number }) de uma mensagem de contato; [] se não houver. */
export const getSharedContacts = (message) => {
  if (!message) return [];
  const data = parseJson(message.dataJson);
  const fromData = fromMessageBag(data?.message);
  if (fromData.length) return fromData;
  const fromWaha = fromMessageBag(data?._data?.Message || data?._data?.message || data?._data);
  if (fromWaha.length) return fromWaha;
  const fromText = fromBody(message.body);
  if (fromText.length) return fromText;
  return fromRawVCards(message.body);
};

// Formato exato do texto salvo (backend helpers/vcard formatSharedContactsBody),
// também no lastMessage antigo, em que as quebras viraram espaço.
const CONTACT_TEXT = /^Contatos? compartilhados?(?: \(\d+\))?:\s*Nome: /;

/**
 * Rótulo curto de um texto de mensagem de contato ("👤 Nome" / "👤 N contatos"),
 * para prévias (lista de tickets, citação, resposta). null se não for contato.
 */
export const sharedContactsLabel = (text) => {
  const raw = String(text || "");
  if (/^👤 /.test(raw)) return raw; // já é o rótulo novo
  const normalized = raw.replace(FORWARDED_PREFIX, "");
  if (!CONTACT_TEXT.test(normalized)) return null;
  const contacts = fromBody(normalized.replace(/\s+(Nome|Número):/g, "\n$1:"));
  if (contacts.length > 1) return `👤 ${contacts.length} contatos`;
  const one = contacts[0];
  return `👤 ${one?.name || (one?.number ? `+${one.number}` : "Contato")}`;
};

/**
 * Número que pode ir num cartão de contato (mesma regra do backend
 * helpers/vcard SHAREABLE_PHONE_RE): brasileiro 55 + DDD + 8/9 dígitos ou
 * estrangeiro com 10 a 13 dígitos. "" para grupo, LID, BSUID, LiveChat etc.
 */
export const shareableNumber = (raw) => {
  const text = String(raw || "").trim().replace(/:\d+(?=@|$)/, "").replace(/@(c\.us|s\.whatsapp\.net)$/i, "");
  if (!/^\+?\d[\d\s().-]*$/.test(text)) return "";
  const digits = text.replace(/\D/g, "");
  return /^(55\d{10,11}|[1-46-9]\d{9,12}|5[0-46-9]\d{8,11})$/.test(digits) ? digits : "";
};

/** Número para exibir: brasileiro formatado; os outros com "+" (já trazem o país). */
export const contactNumberLabel = (digits) => {
  if (!digits) return "";
  const formatted = formatPhoneNumber(digits);
  return formatted !== digits ? formatted : `+${digits}`;
};
